import React, { useEffect, useState } from 'react';
import { MoreVertical, Loader2 } from 'lucide-react';

export interface RowAction {
  id: string;
  label: string;
}

interface Props {
  actions: RowAction[];
  onAction: (id: string) => void;
  loading?: boolean;
}

const RowActionsMenu: React.FC<Props> = ({ actions, onAction, loading = false }) => {
  const [menu, setMenu] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [menu]);

  return (
    <>
      <div className="relative inline-block">
        <button
          type="button"
          disabled={loading}
          onClick={(e) => {
            e.stopPropagation();
            const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
            const menuWidth = 192;
            const menuHeight = actions.length * 36;
            const preferredLeft = rect.right - menuWidth;
            const left = Math.max(8, Math.min(preferredLeft, window.innerWidth - menuWidth - 8));
            const openUp = rect.bottom + menuHeight > window.innerHeight - 8;
            const top = openUp ? rect.top - menuHeight - 4 : rect.bottom + 4;
            setMenu((curr) => (curr ? null : { top, left }));
          }}
          className="inline-flex items-center justify-center rounded border border-gray-200 dark:border-gray-600 p-1.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Row actions"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-4 w-4" />}
        </button>
      </div>
      {menu && (
        <div
          className="fixed z-[100] w-48 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg py-1"
          style={{ top: menu.top, left: menu.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {actions.map((action) => (
            <button
              key={action.id}
              type="button"
              onClick={() => {
                setMenu(null);
                onAction(action.id);
              }}
              className="block w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default RowActionsMenu;
